package com.wintermar.inspection.feature.data.repository

import app.cash.sqldelight.db.QueryResult
import com.apollographql.apollo.ApolloClient
import com.apollographql.apollo.api.Optional
import com.wintermar.inspection.AppDatabase
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.domain.handleAsFlow
import com.wintermar.inspection.core.ui.util.SelectionModel
import com.wintermar.inspection.feature.data.local.db.SqliteCompat
import com.wintermar.inspection.feature.data.remote.mapper.template.toListInspectionTemplateModel
import com.wintermar.inspection.feature.data.remote.mapper.template.toListSelectionModel
import com.wintermar.inspection.feature.data.remote.request.HeaderRequest
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.domain.model.template.TemplateModel
import com.wintermar.inspection.feature.domain.repository.TemplateRepository
import com.wintermar.inspection.graphql.GetSimpleListTemplateQuery
import com.wintermar.inspection.graphql.InspectionTemplatesQuery
import com.wintermar.inspection.graphql.type.InspectionTemplateStatus
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.withContext

private typealias ExecTemplateQuery = (
    id: String,
    name: String,
    version: Long?,
    publishedDate: String,
    numberOfPages: Long?,
    numberOfSections: Long?,
    numberOfQuestions: Long?,
    status: String
) -> QueryResult<Long>

private typealias ExecPageQuery = (
    id: String,
    templateId: String,
    title: String,
    orderPosition: Long
) -> QueryResult<Long>

private typealias ExecSectionQuery = (
    id: String,
    pageId: String,
    title: String,
    orderPosition: Long
) -> QueryResult<Long>

private typealias ExecQuestionQuery = (
    id: String,
    sectionId: String,
    questionText: String,
    guidelines: String?,
    required: Boolean,
    orderPosition: Long,
    answerType: String,
    failedOptionId: String?,
    neutralOptionId: String?
) -> QueryResult<Long>

class TemplateRepositoryImpl(
    private val apolloClient: ApolloClient,
    private val appDatabase: AppDatabase
) : TemplateRepository {
    private fun insertTemplate(
        template: InspectionTemplateModel,
        execTemplateQuery: ExecTemplateQuery,
        execPageQuery: ExecPageQuery,
        execSectionQuery: ExecSectionQuery,
        execQuestionQuery: ExecQuestionQuery
    ) {
        execTemplateQuery(
            template.id,
            template.name,
            template.version.toLong(),
            template.publishedDate,
            template.numberOfPages.toLong(),
            template.numberOfSections.toLong(),
            template.numberOfQuestions.toLong(),
            template.status.name
        )

        template.pages.forEach { page ->
            execPageQuery(
                page.id,
                template.id,
                page.title,
                page.order.toLong()
            )

            page.sections.forEach { section ->
                execSectionQuery(
                    section.id,
                    page.id,
                    section.title,
                    section.order.toLong()
                )

                section.questions.forEach { question ->
                    execQuestionQuery(
                        question.id,
                        section.id,
                        question.questionText,
                        question.guidelines,
                        question.required,
                        question.order.toLong(),
                        question.answerType.name,
                        question.failedOptionId,
                        question.neutralOptionId
                    )
                }
            }
        }
    }

    override fun getTemplates(page: Int, pageSize: Int): Flow<FlowResult<TemplateModel>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        InspectionTemplatesQuery(
                            page,
                            pageSize,
                            status = Optional.Present(InspectionTemplateStatus.PUBLISHED),
                            archived = Optional.Present(false)
                        )
                    )
                    .addHttpHeader(
                        name = HeaderRequest.OPERATION,
                        value = InspectionTemplatesQuery.OPERATION_NAME
                    )
                    .execute()
            }
        ) { response ->
            val data = response.data?.inspectionTemplates
            TemplateModel(
                data = data?.toListInspectionTemplateModel() ?: emptyList(),
                total = data?.totalCount ?: 0
            )
        }
    }

    override fun getTemplatesFromLocalDb(
        page: Int,
        pageSize: Int
    ): Flow<FlowResult<TemplateModel>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val offset = (page - 1) * pageSize.toLong()
                val result = appDatabase.transactionWithResult {
                    val queries = appDatabase.templateTableQueries
                    val items = queries.getTemplatesPaginated(
                        limit = pageSize.toLong(),
                        offset = offset
                    ).executeAsList()
                        .toListInspectionTemplateModel()
                    val total = queries.countTemplates().executeAsOne().toInt()
                    Pair(items, total)
                }
                emit(FlowResult.Success(TemplateModel(result.first, result.second)))
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override suspend fun insertTemplates(templates: List<InspectionTemplateModel>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    val execTemplateQuery: (String, String, Long?, String, Long?, Long?, Long?, String) -> QueryResult<Long> =
                        SqliteCompat.selectInsertImplementation(
                            appDatabase = appDatabase,
                            upsert = appDatabase.templateTableQueries::insertTemplate,
                            replace = appDatabase.templateTableQueries::insertReplaceTemplate
                        )

                    val execPageQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.templatePageTableQueries::insertTemplatePage,
                        replace = appDatabase.templatePageTableQueries::insertReplaceTemplatePage
                    )

                    val execSectionQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.templatePageSectionTableQueries::insertTemplatePageSection,
                        replace = appDatabase.templatePageSectionTableQueries::insertReplaceTemplatePageSection
                    )

                    val execQuestionQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.templatePageSectionQuestionTableQueries::insertTemplatePageSectionQuestion,
                        replace = appDatabase.templatePageSectionQuestionTableQueries::insertReplaceTemplatePageSectionQuestion
                    )

                    templates.forEach { template ->
                        insertTemplate(
                            template,
                            execTemplateQuery,
                            execPageQuery,
                            execSectionQuery,
                            execQuestionQuery
                        )
                    }
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun getTemplateByIdFromLocalDb(id: String): Flow<FlowResult<InspectionTemplateModel>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val result = appDatabase.templateTableQueries.getFullTemplate(id).executeAsList()
                    .toListInspectionTemplateModel()
                emit(FlowResult.Success(result))
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override fun getSimpleTemplatesFromLocalDb(): Flow<FlowResult<List<SelectionModel>>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.templateTableQueries
                        .getAllTemplates().executeAsList().toListSelectionModel()
                }
                emit(FlowResult.Success(result))
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override fun getSimpleTemplates(): Flow<FlowResult<List<SelectionModel>>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        GetSimpleListTemplateQuery(
                            page = 1,
                            pageSize = Constant.ALL_PAGE_SIZE,
                            status = Optional.present(InspectionTemplateStatus.PUBLISHED),
                            archived = Optional.present(false)
                        )
                    ).addHttpHeader(
                        name = HeaderRequest.OPERATION,
                        value = GetSimpleListTemplateQuery.OPERATION_NAME
                    )
                    .execute()
            },
            response = { response ->
                response.data?.inspectionTemplates?.toListSelectionModel() ?: emptyList()
            }
        )
    }

    override suspend fun deleteAllTemplateFromLocalDb(): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.templateTableQueries.deleteAllTemplates()
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }
}
